import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Truck, Edit, Trash2 } from 'lucide-react';

const Dispatch = () => {
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const emptyForm = {
    customer_id: '', customer_name: '', sales_order_id: '', sales_order_number: '',
    items: [{ product_name: '', sku: '', quantity: '', boxes: '', weight: '' }],
    total_boxes: 0, total_weight: '', vehicle_number: '', driver_name: '', driver_phone: '',
    challan_number: '', invoice_number: '', dispatch_date: '', expected_delivery: '', remarks: ''
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [dRes, cRes, oRes] = await Promise.all([
        api.get('/production/dispatches'), api.get('/purchase/customers'), api.get('/sales/orders')
      ]);
      setDispatches(dRes.data); setCustomers(cRes.data); setOrders(oRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const onCustomerChange = (cid) => {
    const c = customers.find(x => x.id === cid);
    setFormData(prev => ({ ...prev, customer_id: cid, customer_name: c?.name || '' }));
  };
  const onOrderChange = (oid) => {
    const o = orders.find(x => x.id === oid);
    if (o) {
      setFormData(prev => ({
        ...prev, sales_order_id: oid, sales_order_number: o.order_number,
        customer_name: o.customer_name,
        items: o.items.map(i => ({ product_name: i.name, sku: i.sku, quantity: String(i.quantity), boxes: '', weight: '' }))
      }));
    }
  };

  const updateItem = (idx, field, val) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      return { ...prev, items };
    });
  };
  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { product_name: '', sku: '', quantity: '', boxes: '', weight: '' }] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/production/dispatches', formData);
      setShowModal(false); setFormData({ ...emptyForm }); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dispatch?')) return;
    try { await api.delete(`/production/dispatches/${id}`); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/production/dispatches/${id}`, { status }); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const getStatusBadge = (s) => {
    const m = { dispatched: 'badge-info', in_transit: 'badge-warning', delivered: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${m[s] || 'badge-info'}`;
  };

  const filtered = dispatches.filter(d =>
    (d.dispatch_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.challan_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.vehicle_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="dispatch-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Dispatch</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Manage outbound shipments and delivery tracking</p>
        </div>
        <button onClick={() => { setFormData({ ...emptyForm }); setShowModal(true); }} className="btn btn-primary" data-testid="create-dispatch-btn"><Plus size={16} /> New Dispatch</button>
      </div>

      <div className="grid grid-cols-4 mb-4">
        {['dispatched', 'in_transit', 'delivered', 'cancelled'].map(st => (
          <div key={st} className="stat-card" data-testid={`stat-${st}`}>
            <div className="stat-value">{dispatches.filter(d => d.status === st).length}</div>
            <div className="stat-label" style={{ textTransform: 'capitalize' }}>{st.replace('_', ' ')}</div>
          </div>
        ))}
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search dispatch #, customer, challan, vehicle..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Dispatch #</th><th>Customer</th><th>SO #</th><th>Vehicle</th><th>Challan</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No dispatches</td></tr> : filtered.map(d => (
                  <tr key={d.id} data-testid={`dispatch-row-${d.dispatch_number}`}>
                    <td className="font-mono">{d.dispatch_number}</td>
                    <td>{d.customer_name || '-'}</td>
                    <td>{d.sales_order_number || '-'}</td>
                    <td>{d.vehicle_number || '-'}</td>
                    <td>{d.challan_number || '-'}</td>
                    <td className="text-sm">{d.dispatch_date || '-'}</td>
                    <td><span className={getStatusBadge(d.status)}>{(d.status || '').replace('_', ' ')}</span></td>
                    <td>
                      <div className="flex gap-2">
                        {d.status === 'dispatched' && <button onClick={() => updateStatus(d.id, 'in_transit')} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>In Transit</button>}
                        {d.status === 'in_transit' && <button onClick={() => updateStatus(d.id, 'delivered')} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>Delivered</button>}
                        <button onClick={() => handleDelete(d.id)} className="btn btn-ghost btn-sm" title="Delete"><Trash2 size={14} color="#dc2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dispatch-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create Dispatch</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Sales Order</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => onOrderChange(e.target.value)} data-testid="order-select">
                      <option value="">Select SO...</option>
                      {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer</label>
                    <select className="form-select" value={formData.customer_id} onChange={(e) => onCustomerChange(e.target.value)} data-testid="customer-select">
                      <option value="">Select...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Dispatch Date</label><input type="text" className="form-input" value={formData.dispatch_date} onChange={(e) => setFormData({ ...formData, dispatch_date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="date-input" /></div>
                  <div className="form-group"><label className="form-label">Vehicle Number *</label><input type="text" className="form-input" value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })} required data-testid="vehicle-input" /></div>
                  <div className="form-group"><label className="form-label">Driver Name</label><input type="text" className="form-input" value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} data-testid="driver-name" /></div>
                  <div className="form-group"><label className="form-label">Driver Phone</label><input type="text" className="form-input" value={formData.driver_phone} onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })} data-testid="driver-phone" /></div>
                  <div className="form-group"><label className="form-label">Challan Number</label><input type="text" className="form-input" value={formData.challan_number} onChange={(e) => setFormData({ ...formData, challan_number: e.target.value })} data-testid="challan-input" /></div>
                  <div className="form-group"><label className="form-label">Invoice Number</label><input type="text" className="form-input" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} data-testid="invoice-input" /></div>
                  <div className="form-group"><label className="form-label">Total Boxes</label><input type="number" className="form-input" value={formData.total_boxes} onChange={(e) => setFormData({ ...formData, total_boxes: parseInt(e.target.value) || 0 })} data-testid="total-boxes" /></div>
                  <div className="form-group"><label className="form-label">Total Weight</label><input type="text" className="form-input" value={formData.total_weight} onChange={(e) => setFormData({ ...formData, total_weight: e.target.value })} placeholder="e.g. 500 Kg" data-testid="total-weight" /></div>
                </div>
                <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '16px 0 8px' }}>Items</h3>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr><th style={{ padding: '6px' }}>Product</th><th style={{ padding: '6px' }}>SKU</th><th style={{ padding: '6px' }}>Qty</th><th style={{ padding: '6px' }}>Boxes</th><th style={{ padding: '6px' }}>Weight</th></tr></thead>
                  <tbody>
                    {formData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.product_name} onChange={(e) => updateItem(idx, 'product_name', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.boxes} onChange={(e) => updateItem(idx, 'boxes', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.weight} onChange={(e) => updateItem(idx, 'weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" onClick={addItem} className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }}><Plus size={12} /> Add Item</button>
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Remarks</label><textarea className="form-input" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} rows={2} data-testid="remarks" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dispatch-btn">Create Dispatch</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dispatch;
